/* Live data hooks. NO FALLBACK ON A PUBLIC SCREEN.
 *
 * THE RULE, and it is Bassem's P0: a failed read shows the design's error
 * state with a retry. It does not show invented listings. It used to — six of
 * them, with prices, one of which advertised a flat in «المهندسين» and placed
 * it in «عين شمس، الجيزة» — behind a "sample data" badge. A badge does not
 * make a fabricated listing honest.
 *
 * `fallback` is therefore OPTIONAL and, on the public hooks, absent. What is
 * left of it is a development convenience behind src/dev/gate.ts, which is
 * dead code in any build.
 *
 * The failure is still reported once to the console with the exact blocked
 * origin, which is the string the proxy owner needs to widen the allowlist.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, type DataSource } from "./client";
import {
  buildMarketData, fetchMarketData, type MarketData,
} from "./markets";
import {
  fetchFeaturedListings, fetchListingById, fetchListings, orderByPromoRank,
  orderForRail, toListing, type Listing, type ListingFilters,
} from "./listings";
import { devFixtures } from "@/dev/gate";
import { fetchPlans, type Plan } from "./plans";
import { getToken } from "@/auth/session";
import { getMyListingById } from "@/api/me";

export type Loadable<T> = {
  data: T | null;
  source: DataSource | null;
  loading: boolean;
  /** Set only when the live attempt failed. Fixtures may still be rendering. */
  failure: ApiError | null;
  reload: () => void;
};

/** Reported once per failing path per session — keeps the console readable. */
const reported = new Set<string>();

export function reportFailure(err: unknown, path: string): ApiError | null {
  const apiErr =
    err instanceof ApiError
      ? err
      : new ApiError("network", (err as Error)?.message ?? "unknown", { path });
  const key = `${apiErr.kind}:${path}`;
  if (!reported.has(key)) {
    reported.add(key);
    console.warn(`[SouqAkar] ${apiErr.report}`);
  }
  return apiErr;
}

function useLoadable<T>(
  load: (signal: AbortSignal) => Promise<T>,
  /* Absent on every public hook. See the note at the top of this file. */
  fallback: (() => Promise<T | null> | T | null) | null,
  path: string,
): Loadable<T> {
  const [data, setData] = useState<T | null>(null);
  const [source, setSource] = useState<DataSource | null>(null);
  const [loading, setLoading] = useState(true);
  const [failure, setFailure] = useState<ApiError | null>(null);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  const fallbackRef = useRef(fallback);

  // Refs are refreshed in an effect rather than during render: writing to
  // ref.current while rendering is unsafe under concurrent rendering. This
  // effect is declared before the fetching one, so it runs first and the
  // fetcher always sees the current closures.
  useEffect(() => {
    loadRef.current = load;
    fallbackRef.current = fallback;
  });

  useEffect(() => {
    const ctrl = new AbortController();
    let live = true;
    setLoading(true);

    loadRef.current(ctrl.signal)
      .then((result) => {
        if (!live) return;
        setData(result);
        setSource("live");
        setFailure(null);
      })
      .catch(async (err) => {
        if (!live || ctrl.signal.aborted) return;
        setFailure(reportFailure(err, path));
        /* No fallback is the normal case: `data` stays null, `failure` is
           set, and the screen shows its error state. */
        if (!fallbackRef.current) return;
        const fb = await fallbackRef.current();
        if (!live || fb == null) return;
        setData(fb);
        setSource("fixture");
      })
      .finally(() => { if (live) setLoading(false); });

    return () => { live = false; ctrl.abort(); };
  }, [path, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  return { data, source, loading, failure, reload };
}

/* Cities and districts. The dev fallback is reference data, not content --
 * real place names from the same seed the database uses -- and it is still
 * behind the dev gate, because "fixtures only in dev" is the whole rule and a
 * rule with an exception is a rule with a hole. */
export function useMarketData(): Loadable<MarketData> {
  return useLoadable<MarketData>(
    (signal) => fetchMarketData(signal),
    async () => {
      const dev = await devFixtures();
      if (!dev) return null;
      const geo = await dev.loadFixtureGeo();
      return buildMarketData(geo.countries, geo.cities, geo.districts);
    },
    "markets",
  );
}

export function useFeaturedListings(limit = 12, countryId?: number | null): Loadable<Listing[]> {
  return useLoadable<Listing[]>(
    (signal) => fetchFeaturedListings(limit, signal, countryId),
    async () => {
      const dev = await devFixtures();
      return dev ? orderForRail(dev.fixtureListings.map(toListing)) : null;
    },
    // The market is part of the key, so switching country refetches the rail.
    `listings:featured:${countryId ?? "all"}`,
  );
}

/* Browse results. Filters go to the server; the fixture fallback applies the
 * same predicate locally so a blocked proxy still behaves like the real one. */
export function useListings(filters: ListingFilters): Loadable<Listing[]> {
  // Serialise the filter set so the effect re-runs when it actually changes.
  const key = JSON.stringify(filters);
  const load = useCallback(
    (signal: AbortSignal) => fetchListings(filters, signal),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  const fallback = useCallback(
    async () => {
      const dev = await devFixtures();
      return dev
        ? orderByPromoRank(dev.fixtureListings.map(toListing).filter((l) => matchesFilters(l, filters)))
        : null;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key],
  );
  return useLoadable<Listing[]>(load, fallback, `listings:${key}`);
}

/** The fixture-side mirror of the server's filtering. */
function matchesFilters(l: Listing, f: ListingFilters): boolean {
  if (f.listingType && l.listingType !== f.listingType) return false;
  if (f.propertyType && l.type !== f.propertyType) return false;
  if (f.propertyTypes?.length && !f.propertyTypes.includes(l.type as never)) return false;
  if (f.cityId != null && l.cityId !== f.cityId) return false;
  if (f.districtId != null && l.districtId !== f.districtId) return false;
  if (f.minPrice != null && (l.price ?? 0) < f.minPrice) return false;
  if (f.maxPrice != null && (l.price ?? Infinity) > f.maxPrice) return false;
  if (f.minBeds != null && (l.beds ?? 0) < f.minBeds) return false;
  if (f.furnished != null && l.furnished !== f.furnished) return false;
  if (f.hasVideo === true && !l.hasVideo) return false;
  return true;
}

/** A single listing, for the detail route. */
export function useListing(id: string | undefined): Loadable<Listing | null> {
  const load = useCallback(
    async (signal: AbortSignal) => {
      const pub = await fetchListingById(id ?? "", signal);
      if (pub) return pub;
      // D-W8: the public read forces status=Active, so an owner following a
      // link to their own Pending or Rejected listing saw "unavailable". Fall
      // back to their OWN row -- /my/listings is scoped to owner_id on the
      // server, so this can only ever return the caller's own listing, and a
      // guest (no token) simply gets null and keeps the honest state.
      if (!getToken()) return null;
      return await getMyListingById(id ?? "", signal).catch(() => null);
    },
    [id],
  );
  const fallback = useCallback(
    async () => {
      const dev = await devFixtures();
      return dev ? dev.fixtureListings.map(toListing).find((l) => l.id === id) ?? null : null;
    },
    [id],
  );
  return useLoadable<Listing | null>(load, fallback, `listing:${id ?? "none"}`);
}

/* Plans. No fixture fallback with invented prices or gates -- if the server
 * is unreachable the result is empty and callers treat the gate as unknown,
 * per standing ruling (a). */
export function usePlans(): Loadable<Plan[]> {
  return useLoadable<Plan[]>(
    (signal) => fetchPlans(signal),
    () => [],
    "plans",
  );
}
